// ════════════════════════════════════════════════════════════════════════
//  KitchenControl — Aanmeldpagina (proefaccount)
//  Self-serve onboarding: maakt een Supabase-gebruiker aan met metadata
//  (bedrijfsnaam, naam, telefoon, voorwaarden-akkoord) die de signup-trigger
//  uitleest om tenant + membership + seed_tenant aan te maken.
//
//  Gebruik in App.jsx (vóór login):
//    <SignUp supabaseClient={supabase} onInloggen={() => setScherm("login")} />
// ════════════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from "react";
import {
  Thermometer, ClipboardList, BookOpen, Check, Eye, EyeOff,
  ArrowRight, Loader2, Mail,
} from "lucide-react";
import { JuridischModal } from "./JuridischeTekst";

// ── Kleur-tokens ──────────────────────────────────────────────────────────
const C = {
  ink:    "#0F172A", // slate-900 — merkpaneel + primaire knop
  ink2:   "#1E293B", // slate-800
  paper:  "#FFFFFF",
  mist:   "#F1F5F9", // slate-100 — veldvulling
  line:   "#E2E8F0", // slate-200 — randen
  text:   "#0F172A",
  muted:  "#64748B", // slate-500
  faint:  "#94A3B8", // slate-400
  accent: "#D97706", // amber-600 — accent (links, focus, mark)
  accentSoft: "#FEF3C7", // amber-100
  ok:     "#16A34A", // green-600 — wachtwoord-eisen voldaan
  okSoft: "#DCFCE7",
  red:    "#DC2626",
};

const MERK_PUNTEN = [
  { icon: Thermometer,   titel: "HACCP zonder papier",   tekst: "Temperaturen, ontvangst en schoonmaak digitaal vastgelegd." },
  { icon: ClipboardList, titel: "Mise en place",          tekst: "Prep-lijsten die je team elke dag bijwerkt." },
  { icon: BookOpen,      titel: "Recepten digitaal",     tekst: "Bereiding, allergenen en bewaaradvies bij de hand." },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignUp({ supabaseClient, onInloggen, inviteCode }) {
  const [bedrijf, setBedrijf]   = useState("");
  const [voornaam, setVoornaam] = useState("");
  const [achternaam, setAchternaam] = useState("");
  const [email, setEmail]       = useState("");
  const [telefoon, setTelefoon] = useState("");
  const [pw, setPw]             = useState("");
  const [pw2, setPw2]           = useState("");
  const [akkoord, setAkkoord]   = useState(false);
  const [toon, setToon]         = useState(false);
  const [bezig, setBezig]       = useState(false);
  const [fout, setFout]         = useState("");
  const [klaar, setKlaar]       = useState(false);
  const [juridischOpen, setJuridischOpen] = useState(null); // null | "voorwaarden" | "privacy"

  // ── Validatie ──
  const pwEisen = useMemo(() => ([
    { label: "8+ tekens",     ok: pw.length >= 8 },
    { label: "Hoofdletter",   ok: /[A-Z]/.test(pw) },
    { label: "Kleine letter", ok: /[a-z]/.test(pw) },
    { label: "Cijfer",        ok: /[0-9]/.test(pw) },
  ]), [pw]);

  const pwGoed = pwEisen.every((e) => e.ok);
  const pwMatch = pw.length > 0 && pw === pw2;
  const emailGoed = EMAIL_RE.test(email);
  const isInvite = !!inviteCode;
  const compleet =
    (isInvite || bedrijf.trim()) && voornaam.trim() && achternaam.trim() &&
    emailGoed && pwGoed && pwMatch && akkoord;

  // Welke velden zijn al "aangeraakt" (leeg gelaten na focus)? Zo tonen we een
  // rode rand pas als iemand een veld daadwerkelijk oversloeg — niet meteen bij
  // het openen van een nog lege pagina.
  const [touched, setTouched] = useState({});
  const raak = (veld) => setTouched((t) => ({ ...t, [veld]: true }));

  // Lijst van wat er nog ontbreekt, in leesbare tekst. Wordt meteen bij de knop
  // getoond zodat de gebruiker precies ziet waarom 'ie nog niet verder kan.
  const ontbreekt = [];
  if (!isInvite && !bedrijf.trim()) ontbreekt.push("naam van je restaurant of keuken");
  if (!voornaam.trim()) ontbreekt.push("voornaam");
  if (!achternaam.trim()) ontbreekt.push("achternaam");
  if (!emailGoed) ontbreekt.push("een geldig e-mailadres");
  if (!pwGoed) ontbreekt.push("een wachtwoord dat aan de eisen voldoet");
  else if (!pwMatch) ontbreekt.push("twee keer hetzelfde wachtwoord");
  if (!akkoord) ontbreekt.push("akkoord met de voorwaarden");

  // ── Aanmelden ──
  const aanmelden = async () => {
    if (!compleet || bezig) return;
    setFout("");
    if (!supabaseClient) { setFout("Supabaseclient ontbreekt — neem contact op."); return; }
    setBezig(true);
    try {
      const { error } = await supabaseClient.auth.signUp({
        email: email.trim(),
        password: pw,
        options: {
          data: {
            ...(isInvite ? { invite_code: inviteCode } : { bedrijfsnaam: bedrijf.trim() }),
            voornaam: voornaam.trim(),
            achternaam: achternaam.trim(),
            telefoon: telefoon.trim(),
            voorwaarden_akkoord: true,
            voorwaarden_akkoord_op: new Date().toISOString(),
          },
        },
      });
      if (error) {
        const m = (error.message || "").toLowerCase();
        if (m.includes("already") || m.includes("registered"))
          setFout("Er bestaat al een account met dit e-mailadres. Log in plaats daarvan in.");
        else if (m.includes("password"))
          setFout("Het wachtwoord voldoet niet aan de eisen.");
        else
          setFout("Aanmelden lukte niet. Probeer het zo nog eens.");
        setBezig(false);
        return;
      }
      setKlaar(true);
    } catch (e) {
      setFout("Er ging iets mis met de verbinding. Probeer het zo nog eens.");
      setBezig(false);
    }
  };

  const onEnter = (e) => { if (e.key === "Enter") aanmelden(); };

  // ── Gedeelde styles ──
  const labelStyle = { display: "block", fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 6, letterSpacing: 0.1 };
  const wrapStyle  = { marginBottom: 16 };
  const foutRand   = { borderColor: "#DC2626", boxShadow: "0 0 0 3px rgba(220,38,38,.12)" };

  return (
    <div className="kc-signup">
      <style>{kcStyle}</style>
      <JuridischModal tonen={juridischOpen} onClose={() => setJuridischOpen(null)} />

      <div className="kc-wrap">
        {/* ─── MERKPANEEL ─── */}
        <div className="kc-brand">
          <div style={{ position: "relative", zIndex: 2 }}>
            {/* Wordmark */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img
                src="/kc-logo.png"
                alt="KitchenControl logo"
                style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }}
              />
              <span style={{ color: C.paper, fontSize: 18, fontWeight: 700, letterSpacing: -0.2 }}>
                KitchenControl
              </span>
            </div>

            {/* Thesis-kop */}
            <h1 className="kc-display" style={{
              color: C.paper, fontSize: 40, lineHeight: 1.08, margin: "44px 0 14px",
              fontWeight: 600, letterSpacing: -0.5, maxWidth: 380,
            }}>
              Overzicht <br />in je keuken.
            </h1>
            <p style={{ color: "#CBD5E1", fontSize: 15.5, lineHeight: 1.5, margin: 0, maxWidth: 360 }}>
              HACCP, mise en place, rooster en recepten — in één app, binnen handbereik.
            </p>

            {/* Waarde-punten */}
            <div className="kc-brand-extra" style={{ marginTop: 38, display: "flex", flexDirection: "column", gap: 18 }}>
              {MERK_PUNTEN.map(({ icon: Icon, titel, tekst }) => (
                <div key={titel} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 8, background: C.ink2, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}>
                    <Icon size={17} color={C.accent} strokeWidth={2} />
                  </div>
                  <div>
                    <div style={{ color: C.paper, fontSize: 14, fontWeight: 700 }}>{titel}</div>
                    <div style={{ color: "#94A3B8", fontSize: 13, lineHeight: 1.45, marginTop: 1 }}>{tekst}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Signatuur: meplijst-screenshot ─── */}
          <div className="kc-brand-extra" style={{ position: "relative", zIndex: 2, marginTop: 40 }}>
            <img
              src="/meplijst.jpeg"
              alt="Voorbeeld van de mise-en-place lijst in KitchenControl"
              style={{
                transform: "rotate(-2.2deg)", width: 240, borderRadius: 12,
                boxShadow: "0 18px 40px -12px rgba(0,0,0,0.55)",
                display: "block",
              }}
            />
          </div>

          {/* Sfeer-gloed achtergrond */}
          <div aria-hidden style={{
            position: "absolute", top: -120, right: -120, width: 360, height: 360,
            borderRadius: "50%", background: "radial-gradient(circle, rgba(217,119,6,0.16), transparent 70%)", zIndex: 1,
          }} />
        </div>

        {/* ─── FORMULIER ─── */}
        <div className="kc-form-side">
          <div className="kc-form-card kc-fade">
            {!klaar ? (
              <>
                <h2 className="kc-display" style={{ fontSize: 28, fontWeight: 600, color: C.text, margin: 0, letterSpacing: -0.4 }}>
                  {isInvite ? "Je bent uitgenodigd" : "Start je proefperiode"}
                </h2>
                <p style={{ fontSize: 14.5, color: C.muted, margin: "8px 0 0" }}>
                  {isInvite ? "Maak je eigen login aan om bij je team aan de slag te gaan." : "14 dagen gratis · geen creditcard · klaar in een minuut."}
                </p>
                <p style={{ fontSize: 13.5, color: C.muted, margin: "10px 0 26px" }}>
                  Al een account?{" "}
                  <span className="kc-link" role="button" tabIndex={0}
                    onClick={() => onInloggen && onInloggen()}
                    onKeyDown={(e) => e.key === "Enter" && onInloggen && onInloggen()}>
                    Inloggen
                  </span>
                </p>

                {/* Bedrijfsnaam (niet bij een uitnodiging) */}
                {!isInvite && (
                  <div style={wrapStyle}>
                    <label style={labelStyle}>Naam van je restaurant of keuken</label>
                    <input className="kc-inp" value={bedrijf} onChange={(e) => setBedrijf(e.target.value)}
                      onBlur={() => raak("bedrijf")}
                      style={touched.bedrijf && !bedrijf.trim() ? foutRand : undefined}
                      onKeyDown={onEnter} placeholder="Bijv. Restaurant De Kade" autoFocus />
                  </div>
                )}

                {/* Voor- + achternaam */}
                <div style={{ ...wrapStyle, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Voornaam</label>
                    <input className="kc-inp" value={voornaam} onChange={(e) => setVoornaam(e.target.value)}
                      onBlur={() => raak("voornaam")}
                      style={touched.voornaam && !voornaam.trim() ? foutRand : undefined}
                      onKeyDown={onEnter} placeholder="Voornaam" />
                  </div>
                  <div>
                    <label style={labelStyle}>Achternaam</label>
                    <input className="kc-inp" value={achternaam} onChange={(e) => setAchternaam(e.target.value)}
                      onBlur={() => raak("achternaam")}
                      style={touched.achternaam && !achternaam.trim() ? foutRand : undefined}
                      onKeyDown={onEnter} placeholder="Achternaam" />
                  </div>
                </div>

                {/* E-mail */}
                <div style={wrapStyle}>
                  <label style={labelStyle}>E-mailadres</label>
                  <input className="kc-inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => raak("email")}
                    style={touched.email && !emailGoed ? foutRand : undefined}
                    onKeyDown={onEnter} placeholder="jij@jouwzaak.nl" />
                </div>

                {/* Telefoon */}
                <div style={wrapStyle}>
                  <label style={labelStyle}>Telefoon <span style={{ color: C.faint, fontWeight: 500 }}>· optioneel</span></label>
                  <input className="kc-inp" value={telefoon} onChange={(e) => setTelefoon(e.target.value)} onKeyDown={onEnter} placeholder="06 12345678" />
                </div>

                {/* Wachtwoord */}
                <div style={wrapStyle}>
                  <label style={labelStyle}>Wachtwoord</label>
                  <div style={{ position: "relative" }}>
                    <input className="kc-inp" type={toon ? "text" : "password"} value={pw}
                      onChange={(e) => setPw(e.target.value)} onKeyDown={onEnter} placeholder="Kies een wachtwoord"
                      style={{ paddingRight: 44 }} />
                    <button type="button" onClick={() => setToon((t) => !t)} className="kc-eye" aria-label="Toon wachtwoord">
                      {toon ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {/* Eisen als chips */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 9 }}>
                    {pwEisen.map((e) => (
                      <span key={e.label} style={{
                        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600,
                        padding: "3px 8px 3px 6px", borderRadius: 20,
                        color: e.ok ? C.ok : C.faint, background: e.ok ? C.okSoft : C.mist,
                        transition: "all .15s",
                      }}>
                        <Check size={12} strokeWidth={3} style={{ opacity: e.ok ? 1 : 0.35 }} />
                        {e.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Bevestig wachtwoord */}
                <div style={wrapStyle}>
                  <label style={labelStyle}>Bevestig wachtwoord</label>
                  <input className="kc-inp" type={toon ? "text" : "password"} value={pw2}
                    onChange={(e) => setPw2(e.target.value)} onKeyDown={onEnter} placeholder="Nogmaals het wachtwoord"
                    style={pw2.length > 0 && !pwMatch ? { borderColor: C.red } : {}} />
                  {pw2.length > 0 && !pwMatch && (
                    <div style={{ fontSize: 12, color: C.red, marginTop: 5 }}>De wachtwoorden komen niet overeen.</div>
                  )}
                </div>

                {/* Akkoord */}
                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", margin: "4px 0 20px" }}>
                  <input type="checkbox" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)}
                    style={{ width: 17, height: 17, marginTop: 1, accentColor: C.accent, flexShrink: 0, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.45 }}>
                    Ik ga akkoord met de <span className="kc-link" role="button" tabIndex={0} onClick={(e) => { e.preventDefault(); setJuridischOpen("voorwaarden"); }}>Algemene voorwaarden</span> en de <span className="kc-link" role="button" tabIndex={0} onClick={(e) => { e.preventDefault(); setJuridischOpen("privacy"); }}>Privacyverklaring</span>.
                  </span>
                </label>

                {fout && (
                  <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
                    fontSize: 13, borderRadius: 8, padding: "10px 12px", marginBottom: 16 }}>
                    {fout}
                  </div>
                )}

                {/* Wat ontbreekt er nog? Meteen zichtbaar zodat duidelijk is
                    waarom de knop nog niet actief is. */}
                {!compleet && ontbreekt.length > 0 && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#FFFBEB",
                    border: "1px solid #FDE68A", color: "#92400E", fontSize: 12.5, lineHeight: 1.5,
                    borderRadius: 8, padding: "9px 12px", marginBottom: 12 }}>
                    <span style={{ flexShrink: 0 }}>ℹ️</span>
                    <span>
                      {ontbreekt.length === 1
                        ? <>Vul nog in: <b>{ontbreekt[0]}</b>.</>
                        : <>Nog in te vullen: {ontbreekt.map((x, i) => (
                            <span key={i}><b>{x}</b>{i < ontbreekt.length - 1 ? (i === ontbreekt.length - 2 ? " en " : ", ") : "."}</span>
                          ))}</>}
                    </span>
                  </div>
                )}

                {/* Knop */}
                <button onClick={aanmelden} disabled={!compleet || bezig} className="kc-cta"
                  style={{ opacity: compleet && !bezig ? 1 : 0.5, cursor: compleet && !bezig ? "pointer" : "not-allowed" }}>
                  {bezig
                    ? <><Loader2 size={18} className="kc-spin" /> Account aanmaken…</>
                    : <>Account aanmaken <ArrowRight size={18} strokeWidth={2.4} /></>}
                </button>

                <p style={{ fontSize: 12, color: C.faint, textAlign: "center", margin: "16px 0 0", lineHeight: 1.5 }}>
                  Na 14 dagen stopt de proef vanzelf — geen automatische afschrijving.
                </p>
              </>
            ) : (
              /* ─── Succes — bevestig je mail ─── */
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.accentSoft,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                  <Mail size={28} color={C.accent} strokeWidth={2.2} />
                </div>
                <h2 className="kc-display" style={{ fontSize: 26, fontWeight: 600, color: C.text, margin: "0 0 10px", letterSpacing: -0.3 }}>
                  Bevestig nog even je e-mail
                </h2>
                <p style={{ fontSize: 14.5, color: C.muted, lineHeight: 1.55, margin: "0 auto 18px", maxWidth: 360 }}>
                  We hebben een bevestigingsmail gestuurd naar <b style={{ color: C.text }}>{email}</b>.
                  Open die mail en klik op de link om je account te activeren — daarna kun je aan de slag.
                </p>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, background: C.mist,
                  border: `1px solid ${C.line}`, borderRadius: 10, padding: "12px 14px", margin: "0 auto",
                  maxWidth: 360, textAlign: "left" }}>
                  <b style={{ color: C.text }}>Geen mail ontvangen?</b> Kijk even in je map met
                  ongewenste e-mail of spam. De mail kan een minuut onderweg zijn.
                </div>
                <p style={{ fontSize: 12.5, color: C.faint, margin: "20px 0 0" }}>
                  Mail al bevestigd?{" "}
                  <span className="kc-link" role="button" tabIndex={0}
                    onClick={() => onInloggen && onInloggen()}
                    onKeyDown={(e) => e.key === "Enter" && onInloggen && onInloggen()}>
                    Inloggen
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Scoped CSS: fonts, responsive, focus, animaties ──
const kcStyle = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap');
.kc-signup { min-height: 100vh; width: 100%; background: ${C.paper};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: ${C.text}; -webkit-font-smoothing: antialiased; }
.kc-display { font-family: "Fraunces", Georgia, "Times New Roman", serif; }
.kc-wrap { display: grid; grid-template-columns: 42% 58%; min-height: 100vh; }
.kc-brand { background: ${C.ink}; padding: 52px 48px; position: relative; overflow: hidden;
  display: flex; flex-direction: column; justify-content: space-between; }
.kc-form-side { display: flex; align-items: center; justify-content: center; padding: 48px 32px; }
.kc-form-card { width: 100%; max-width: 460px; }
.kc-inp { width: 100%; box-sizing: border-box; font-family: inherit; font-size: 14.5px;
  padding: 11px 13px; border: 1px solid ${C.line}; border-radius: 9px; background: ${C.paper};
  color: ${C.text}; outline: none; transition: border-color .15s, box-shadow .15s; }
.kc-inp::placeholder { color: ${C.faint}; }
.kc-inp:focus { border-color: ${C.accent}; box-shadow: 0 0 0 3px rgba(217,119,6,0.15); }
.kc-eye { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); background: none;
  border: none; color: ${C.faint}; cursor: pointer; padding: 8px; display: flex; }
.kc-eye:hover { color: ${C.muted}; }
.kc-link { color: ${C.accent}; font-weight: 600; cursor: pointer; text-decoration: none; }
.kc-link:hover { text-decoration: underline; }
.kc-cta { width: 100%; box-sizing: border-box; display: flex; align-items: center; justify-content: center;
  gap: 8px; background: ${C.ink}; color: ${C.paper}; font-family: inherit; font-size: 15px; font-weight: 700;
  border: none; border-radius: 9px; padding: 14px 18px; transition: transform .12s, background .15s; }
.kc-cta:not(:disabled):hover { background: ${C.ink2}; transform: translateY(-1px); }
.kc-fade { animation: kcUp .5s cubic-bezier(.2,.7,.3,1) both; }
.kc-spin { animation: kcSpin .8s linear infinite; }
@keyframes kcUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes kcSpin { to { transform: rotate(360deg); } }
@media (max-width: 880px) {
  .kc-wrap { grid-template-columns: 1fr; }
  .kc-brand { padding: 32px 28px; min-height: auto; }
  .kc-brand-extra { display: none !important; }
  .kc-form-side { padding: 32px 22px 56px; }
}
@media (prefers-reduced-motion: reduce) {
  .kc-fade, .kc-spin, .kc-cta { animation: none !important; transition: none !important; }
}
`;
